/**
 * The one place that decides what a lookup rule looks like on the wire.
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
 *
 * This module exists because of a defect that cost a whole feature: the desktop
 * sent `host` and `port`, the engine read `baseUrl`, nothing was ever dispatched,
 * and both test suites passed because each side was measured against its own idea
 * of the payload. Nothing here is a description of the contract — every request
 * the main process puts on the engine's stdin is built by a function below, and
 * `tests/lookup-ipc-tests/run.mjs` writes what these functions produce to
 * `tests/lookup-ipc-tests/desktop-payload.json`, which
 * `tests/document-engine-lookup-interop-tests` then reads with the engine's own
 * `EngineRequest` and the engine's own serializer options. A rename on either
 * side fails one of those two suites.
 *
 * The other reason it is separate from `main.ts`: `main.ts` cannot be imported
 * without Electron, so a payload built inline there is a payload nothing can test.
 */

import { lookupSpellingSwitchIds } from './desktop-api';
import type {
  FieldLookupAssignment,
  JobFieldDefinition,
  LookupFitRequest,
  LookupReturn,
  LookupRuleDefinition,
  MatchLadderSettings,
  SpellingSwitches,
} from './desktop-api';

/**
 * Every switch stated, always.
 *
 * `normalisation` is optional on `MatchLadderSettings` only so that a screen
 * mid-rebuild still compiles. It is never optional on the wire: a rung whose
 * spelling rules were left to the engine's own record defaults is a rung the screen
 * is describing without owning, which is the arrangement the specification calls
 * "the screen is lying".
 */
export type NormalisedMatchLadder = Omit<MatchLadderSettings, 'normalisation'> & {
  normalisation: SpellingSwitches;
};

/** What actually goes across: every return stated, and the ladder fully stated. */
export type CanonicalLookupRule = Omit<
  LookupRuleDefinition,
  'returns' | 'matchLadder' | 'valueKind'
> & {
  returns: LookupReturn[];
  matchLadder: NormalisedMatchLadder;
};

/** Defaults for anything a caller left unstated. Mirrors the engine's `MatchLadderSettings` record defaults. */
export const LOOKUP_LADDER_DEFAULTS = {
  exact: true,
  characterConfusion: false,
  fuzzy: false,
  minimumScore: 0.92,
  runnerUpMargin: 0.05,
} as const;

/**
 * Which switches the first rung has for this kind of value.
 *
 * A kind the shell does not recognise gets the name switches rather than none: an
 * empty set would send a rung with nothing stated about it, which is the silence
 * this module exists to prevent.
 */
export const spellingSwitchIdsFor = (valueKind: string | null | undefined): readonly string[] =>
  text(valueKind).toLowerCase() === 'code'
    ? lookupSpellingSwitchIds.code
    : lookupSpellingSwitchIds.name;

/**
 * A refusal an operator can read.
 *
 * Everything thrown from this module is one of these, and every message names the
 * rule and says what to do about it. A stack trace reaching a screen is a bug
 * report; a sentence naming the rule is something someone can act on.
 */
export class LookupRuleError extends Error {
  constructor(
    message: string,
    readonly ruleId: string,
    readonly ruleName: string,
  ) {
    super(message);
    this.name = 'LookupRuleError';
  }
}

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const bool = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;
const ratio = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : fallback;

/** The rules a field is subject to, with the empty ones dropped. */
export const fieldLookups = (field: Pick<JobFieldDefinition, 'lookups'>): FieldLookupAssignment[] =>
  (field.lookups ?? []).filter((entry) => text(entry?.ruleId));

/** The lookup the setup screens show for a field: its first, or null when its value is read from the page. */
export const fieldLookup = (
  field: Pick<JobFieldDefinition, 'lookups'>,
): FieldLookupAssignment | null => fieldLookups(field)[0] ?? null;

/**
 * Whether the field is saved with the list's own spelling of its reading.
 *
 * That is a field whose own reading finds the row and which is filled from the very
 * column it matched on — the engine writes the list's spelling back in place. Returns
 * are matched to fields by name, as the engine matches them.
 */
export const usesListSpelling = (
  field: Pick<JobFieldDefinition, 'id' | 'name'>,
  assignment: Pick<FieldLookupAssignment, 'sourceFieldId'> | null | undefined,
  rule: Pick<LookupRuleDefinition, 'findColumn' | 'returns'> | null | undefined,
): boolean =>
  !!assignment &&
  !!rule &&
  (!text(assignment.sourceFieldId) || text(assignment.sourceFieldId) === field.id) &&
  (rule.returns ?? []).some(
    (entry) =>
      entry.column === rule.findColumn &&
      text(entry.fieldName).toLowerCase() === text(field.name).toLowerCase(),
  );

/** What to call a rule in a sentence when it has no name yet, which is every rule before its first save. */
const nameOf = (rule: Pick<LookupRuleDefinition, 'name' | 'id'>): string =>
  text(rule.name) || (text(rule.id) ? `the rule ${text(rule.id)}` : 'this rule');

/**
 * Fills in whatever was left unsaid, clamps the two dials, and states every switch.
 *
 * A switch the caller did not mention is sent as on, which is what the screen shows
 * for it. A switch the caller mentioned that this kind does not have is dropped: it
 * is left over from the other kind, and carrying `legal-suffix` on a rule matching
 * codes would have the engine answering a question about a column that has none.
 */
export function normalizeMatchLadder(
  ladder: Partial<MatchLadderSettings> | null | undefined,
  valueKind: string | null | undefined,
): NormalisedMatchLadder {
  const given = ladder ?? {};
  const stated = given.normalisation ?? {};
  const normalisation: SpellingSwitches = {};
  for (const id of spellingSwitchIdsFor(valueKind)) normalisation[id] = bool(stated[id], true);
  return {
    exact: bool(given.exact, LOOKUP_LADDER_DEFAULTS.exact),
    characterConfusion: bool(given.characterConfusion, LOOKUP_LADDER_DEFAULTS.characterConfusion),
    fuzzy: bool(given.fuzzy, LOOKUP_LADDER_DEFAULTS.fuzzy),
    minimumScore: ratio(given.minimumScore, LOOKUP_LADDER_DEFAULTS.minimumScore),
    runnerUpMargin: ratio(given.runnerUpMargin, LOOKUP_LADDER_DEFAULTS.runnerUpMargin),
    normalisation,
  };
}

/**
 * The returns, checked to the standard the specification sets for them.
 *
 * A rule resolves one row and projects every return off it, so two returns
 * writing the same field is not a duplicate — it is two values arriving at one
 * box with nothing to say which won. It is refused here rather than resolved by
 * order, because resolving it by order is how one of them disappears in silence.
 */
export function normalizeLookupReturns(
  rule: Pick<LookupRuleDefinition, 'id' | 'name' | 'returns'>,
): LookupReturn[] {
  const given = Array.isArray(rule.returns) ? rule.returns : [];
  const returns: LookupReturn[] = [];
  const fields = new Set<string>();
  for (const entry of given) {
    const column = text(entry?.column);
    const fieldName = text(entry?.fieldName);
    const fieldId = text(entry?.fieldId) || null;
    // Neither half is optional: a return naming a column and no field fills
    // nothing, and one naming a field and no column has nowhere to read from. A
    // field id alone is not a field name — the contract is bound to the name, and
    // inventing one from an id the shell cannot resolve would be a guess.
    if (!column || !fieldName)
      throw new LookupRuleError(
        `${nameOf(rule)} has an incomplete entry: every field it fills needs both a column to read and the name of the field to write.`,
        text(rule.id),
        text(rule.name),
      );
    const key = fieldName.toLowerCase();
    if (fields.has(key))
      throw new LookupRuleError(
        `${nameOf(rule)} fills “${fieldName}” twice. One row cannot answer the same field two ways. Remove one.`,
        text(rule.id),
        text(rule.name),
      );
    fields.add(key);
    returns.push(fieldId ? { column, fieldName, fieldId } : { column, fieldName });
  }
  if (returns.length === 0)
    throw new LookupRuleError(
      `${nameOf(rule)} fills no fields. Choose at least one column and the field it fills before saving.`,
      text(rule.id),
      text(rule.name),
    );
  return returns;
}

/** The rule as the engine will receive it: returns as a list and the ladder fully stated. */
export function canonicalLookupRule(rule: LookupRuleDefinition): CanonicalLookupRule {
  const dataTableId = text(rule.dataTableId);
  if (!dataTableId)
    throw new LookupRuleError(
      `${nameOf(rule)} has no list behind it. Import a table, or choose one already imported.`,
      text(rule.id),
      text(rule.name),
    );
  const findColumn = text(rule.findColumn);
  if (!findColumn)
    throw new LookupRuleError(
      `${nameOf(rule)} does not say which column is matched against.`,
      text(rule.id),
      text(rule.name),
    );
  const returns = normalizeLookupReturns(rule);
  const ladder = normalizeMatchLadder(rule.matchLadder, rule.valueKind);
  return {
    id: text(rule.id),
    jobId: text(rule.jobId),
    name: text(rule.name),
    dataTableId,
    findColumn,
    returns,
    // No `valueKind`. The engine dropped it from `LookupRuleDefinition` on purpose
    // — the kind of value a column holds is a property of the column, read off the
    // column itself, and asking an operator for it was asking a question whose
    // whole effect happened somewhere they could not see. The shell keeps its own
    // copy on `LookupRuleDefinition` because it decides which spelling switches the
    // first rung has, which is a question about the screen and not about the row.
    matchLadder: ladder,
    ...(rule.createdAt ? { createdAt: rule.createdAt } : {}),
    ...(rule.updatedAt ? { updatedAt: rule.updatedAt } : {}),
  };
}

// --------------------------------------------------------------- engine requests
//
// Everything below is literally what goes onto the engine's stdin. Read by the
// interop suite; do not inline any of it back into a handler.

export const engineSaveLookupRuleRequest = (jobId: string, rule: LookupRuleDefinition) => ({
  jobId,
  lookupRule: canonicalLookupRule(rule),
});

export const engineTestLookupRequest = (request: {
  jobId: string;
  value: string;
  ruleId?: string;
  rule?: LookupRuleDefinition;
}) => ({
  jobId: request.jobId,
  lookupValue: request.value,
  lookupRuleId: request.ruleId,
  // Setup measures a rule before it is committed, so an unsaved one crosses whole.
  lookupRule: request.rule ? canonicalLookupRule(request.rule) : undefined,
});

export const engineValidateLookupRuleRequest = (request: {
  jobId: string;
  ruleId?: string;
  rule?: LookupRuleDefinition;
  sampleSize?: number;
}) => ({
  jobId: request.jobId,
  lookupRuleId: request.ruleId,
  lookupRule: request.rule ? canonicalLookupRule(request.rule) : undefined,
  sampleSize: request.sampleSize,
});

/**
 * The question asked before anything is tested: can a value out of this field ever
 * be a value in that column?
 *
 * The answer is knowable from the two columns alone, which is why nothing here is
 * a match attempt and no rule need exist yet. `lookupSourceValues` is what the
 * field has actually been read to hold; it is optional because a field that has
 * never been read still has a declared kind, and the engine can answer from the
 * column and the kind alone.
 */
export const engineFieldCanMatchColumnRequest = (request: LookupFitRequest) => ({
  jobId: request.jobId,
  dataTableId: request.dataTableId,
  // `query` carries the column because that is the slot the engine's own
  // `EngineRequest` already has for "the one string this command is about"; a new
  // member for it would be a second spelling of a thing that has one.
  query: request.column,
  // `lookupSourceFieldName`, not an id: this is the spelling the engine's
  // `EngineRequest` declares. A field name is not stable under renaming, which is
  // noted where the request type is declared — but one side inventing the stable
  // spelling on its own is exactly how nothing is ever dispatched.
  lookupSourceFieldName: text(request.sourceFieldName) || undefined,
  lookupSourceValues:
    request.sourceValues && request.sourceValues.length > 0 ? request.sourceValues : undefined,
});

/**
 * What the rule as it stands costs, over this field's own recent readings.
 *
 * `sourceFieldId` is required and is the whole point: the number beside a rung is
 * "fills 31 of 40", and the 40 are readings of that field on this job's documents.
 * A measurement taken against the table itself is a different measurement of a
 * different thing and must not be shown under this sentence.
 */
export const engineMeasureRecentReadingsRequest = (request: {
  jobId: string;
  sourceFieldName: string;
  ruleId?: string;
  rule?: LookupRuleDefinition;
  readingCount?: number;
}) => ({
  jobId: request.jobId,
  lookupSourceFieldName: text(request.sourceFieldName),
  lookupRuleId: request.ruleId,
  lookupRule: request.rule ? canonicalLookupRule(request.rule) : undefined,
  sampleSize: request.readingCount,
});
